/* eslint-disable max-len */
import {
  IconButton,
  Modal,
} from '@material-ui/core';
import { AccessTime } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PF2MDataTable from '~/components/PF2MDataTable';
import usePlatformConfiguration from '~/services/platformConfiguration';
import RenderDistanceManagerField from '../TransportList/RenderDistanceManagerField';
import { searchBulkEditTransportList } from '~/store/dailyparts/actions';
import PF2MButton from '~/components/PF2MButton';
import styles from './styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import EditHistoryButton from '~/components/EditHistoryButton';
import { usePagination } from '../hooks';
import TransportBulkUpdate from './TransportBulkUpdateModal';

const ESCAVATOR_ID = 1;
const LOADER_ID = 15;
const ROCK_FILL_ID = 29;

const BulkUpdateTransportReport = () => {
  const { t: translate } = useTranslation();

  const filters = useSelector(state => state.dailyParts.filters);

  const transportList = useSelector(
    state => state.dailyParts.bulkEditTransportList,
  ).filter(
    ({ exception_type }) => ![10, 16].includes(exception_type),
  );

  const originalBulkEditTransportList = useSelector(
    state => state.dailyParts.originalBulkEditTransportList,
  ).filter(
    ({ exception_type }) => ![10, 16].includes(exception_type),
  );

  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(
    state => state.manager.equipmentsGroups,
  );
  const equipments = useSelector(state => state.manager.equipments);

  const operatorsGroups = useSelector(
    state => state.manager.operatorsGroups.filter(o => o.name),
    shallowEqual,
  );
  const operators = useSelector(
    state => state.manager.operators.filter(o => o.name),
    shallowEqual,
  );

  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const materials = useSelector(state => state.manager.materials);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const sleepLevels = useSelector(state => state.manager.sleepLevels);
  const dispatch = useDispatch();
  const transportOperationTypes = useSelector(
    state => state.manager.transportOperationTypes,
  );
  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);
  const [openModal, setOpenModal] = useState(false);


  const bulkEditTransportReportPagination = useSelector(
    state => state.dailyParts.pagination.bulkEditTransportReportPagination,
  );

  const { value: editLoadDefaultValue } = usePlatformConfiguration('edit_load_default');

  const loadDefault = useSelector(state => state.manager.loads);

  const calculateDMT = ({ full_distance: fd, empty_distance: ed }) => (fd + ed) / 2;

  const columns = [
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'never',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
    },
    {
      editable: 'never',
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
    },
    {
      editable: 'never',
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
    },
    {
      editable: 'never',
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
    },
    {
      editable: 'never',
      title: translate('common:EquipmentType'),
      field: 'id_equip',
      lookup: equipmentTypes,
      customType: 'filterLookup',
    },
    {
      editable: 'never',
      customType: 'filterLookup',
      title: translate('common:EquipmentGroup'),
      field: 'id_group',
      lookup: equipmentsGroups,
      lookupFilter: (e, f) => String(e.id_equipament) === String(f.id_equip)
        || String(f.id_equip) === '0',
    },
    {
      editable: 'never',
      title: translate('common:Equipment'),
      customType: 'filterLookup',
      field: 'id_truck',
      lookup: equipments,
      lookupFilter: (e, f) => String(e.id_group) === String(f.id_group) || String(f.id_group) === '0',
    },
    {
      editable: 'never',
      title: translate('common:Origin'),
      customType: 'filterLookup',
      field: 'id_mine',
      lookup: elements,
    },
    {
      editable: 'never',
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      customType: 'filterLookup',
      lookup: subElements,
      lookupFilter: (e, f) => String(e.id_element) === String(f.id_mine) || String(f.id_mine) === '0',
    },
    {
      editable: 'never',
      title: translate('common:Destination'),
      customType: 'filterLookup',
      field: 'id_destination',
      lookup: elements,
    },
    {
      editable: 'never',
      title: translate('common:DestinationPoint'),
      customType: 'filterLookup',
      field: 'id_destination_point',
      lookup: subElements,
      lookupFilter: (option, row) => String(option.id_element) === String(row.id_destination)
        || String(row.id_destination) === '0',
    },
    {
      editable: 'never',
      title: translate('common:CycleStageTimes'),
      field: 'cycle_stage_times',
      render: _row => (
        <IconButton style={{ outline: 'none', cursor: 'default' }}>
          <AccessTime style={{ color: 'rgb(242 170 0)' }} />
        </IconButton>
      ),
    },
    {
      editable: 'never',
      title: translate('common:Material'),
      customType: 'filterLookup',
      field: 'id_material_truck',
      lookup: materials,
    },
    {
      editable: 'never',
      title: translate('common:CycleTime'),
      field: 'duration',
    },
    {
      editable: 'never',
      title: translate('common:LoadTime'),
      field: 'load_time',
    },
    {
      editable: 'never',
      title: translate('common:UnloadTime'),
      field: 'unload_time',
    },
    {
      editable: 'never',
      title: translate('common:LoadQueueTime'),
      field: 'load_queue_time',
    },
    {
      editable: 'never',
      title: translate('common:UnloadQueueTime'),
      field: 'unload_queue_time',
    },
    {
      editable: 'never',
      title: translate('common:LoadManeuverTime'),
      field: 'load_maneuver_time',
    },
    {
      editable: 'never',
      title: translate('common:UnloadManeuverTime'),
      field: 'unload_maneuver_time',
    },
    {
      editable: 'never',
      title: translate('common:EmptyTrafficTime'),
      field: 'empty_time',
    },
    {
      editable: 'never',
      title: translate('common:FullTrafficTime'),
      field: 'full_time',
    },
    {
      editable: 'never',
      title: translate('common:EmptyTrafficDistance'),
      field: 'empty_distance',
    },
    {
      editable: 'never',
      title: translate('common:FullTrafficDistance'),
      field: 'full_distance',
    },
    {
      editable: 'never',
      title: translate('common:MaintenanceStopTime'),
      field: 'code_time',
    },
    {
      editable: 'never',
      title: translate('common:OperatorGroup'),
      field: 'id_operator_truck_group',
      lookup: operatorsGroups,
      customType: 'filterLookup',
    },
    {
      editable: 'never',
      title: translate('common:Operator'),
      customType: 'filterLookup',
      field: 'id_operator_truck',
      lookup: operators,
      lookupFilter: (e, f) => String(e.id_group) === String(f.id_operator_truck_group)
        || String(f.id_operator_truck_group) === '0',
      lookupKey: 'id_operator',
    },
    {
      editable: 'never',
      title: translate('common:Shift'),
      field: 'turn',
      lookup: turns,
      customType: 'filterLookup',
    },
    {
      editable: 'never',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      customType: 'filterLookup',
    },
    {
      editable: 'never',
      title: translate('common:LoadDefault'),
      field: 'load_manager',
      lookup: editLoadDefaultValue === 0 ? loadDefault : null,
      customType: editLoadDefaultValue === 0 ? 'filterLookup' : null,
      lookupKey: 'name',
    },
    {
      editable: 'never',
      title: translate('common:LoadBalance'),
      field: 'load_balance',
    },
    {
      editable: 'never',
      title: translate('common:LoadEquipment'),
      customType: 'filterLookup',
      lookup: equipments.filter(equip => [ESCAVATOR_ID, LOADER_ID, ROCK_FILL_ID].includes(equip.id_equip)),
      field: 'id_load_equip',
    },
    {
      editable: 'never',
      title: translate('common:CycleType'),
      field: 'exception_type',
      lookup: exceptionTypes,
    },
    {
      editable: 'never',
      title: translate('common:DMT'),
      field: 'distance_manager',
      render: calculateDMT,
    },
    {
      editable: 'never',
      title: translate('common:SleepLevel'),
      field: 'sleep_level',
      lookup: sleepLevels,
    },
    {
      editable: 'never',
      title: translate('common:StepsPerformedByAutomation'),
      field: 'automation_toggle_is_disable',
      render: row => <RenderDistanceManagerField transportReport={row} />,
    },
    {
      editable: 'never',
      title: translate('common:OperatorLoad'),
      field: 'id_operator_load',
      lookup: operators,
      customType: 'filterLookup',
      lookupKey: 'id_operator',
    },
    {
      editable: 'never',
      customType: 'filterLookup',
      title: translate('common:OperationType'),
      field: 'transport_operation_type',
      lookup: transportOperationTypes,
    },
  ];

  const initialFormData = {
    ...columns.reduce((acc, e) => {
      if (e.required) return { ...acc, [e.field]: '' };
      return { ...acc, [e.field]: '0' };
    }, {}),
    unloaded_stop_time: 0,
    loaded_stop_time: 0,
    is_special: 0,
    id_mine_manager: 0,
    id_mine_origin_manager: 0,
  };


  useEffect(() => {
    if (transportList) {
      document.querySelectorAll('th').forEach((thElement) => {
        const newTh = thElement;
        if (newTh.textContent.includes(`${translate('common:Edit')}`)) {
          newTh.textContent = '';
        }
      });
    }
  }, [transportList]);

  const paginationOptions = usePagination({
    filters,
    dispatch,
    listType: 'bulkEditTransportReport',
    pagination: bulkEditTransportReportPagination,
    searchAction: searchBulkEditTransportList,
  });

  const { page = 0, total = 0, loadingMore = false } = bulkEditTransportReportPagination || { };

  return (
    <div style={styles.container}>
      <div style={styles.formControlContainer}>
        <PF2MButton
          color="secondary"
          disabled={transportList?.length === 0 || !transportList}
          onClick={() => setOpenModal(true)}
        >
          {translate('common:EditFields')}
        </PF2MButton>

      </div>
      <PF2MDataTable
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: false,
          search: false,
          selection: false,
          cacheKey: 'daily_parts_transport',
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
          hideCommit: true,
          hideReload: true,
          hideAdd: true,
          hideDelete: false,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        defaultEditable={false}
        initialFormData={initialFormData}
        columns={columns}
        data={transportList.length > 0 ? transportList : []}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="transport"
          referenceColumns={columns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
      {openModal && (
        <TransportBulkUpdate
          open={openModal}
          setOpen={setOpenModal}
          filters={filters}
          bulkUpdateList={transportList}
          originalList={originalBulkEditTransportList}
          pagination={bulkEditTransportReportPagination}
        />
      )}
    </div>
  );
};

export default BulkUpdateTransportReport;
